import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { DollarSign, Eye, CreditCard, ArrowRight, Star, QrCode, Calculator, Zap } from 'lucide-react';
import mockData from '../data/mock';

const DriverSection = () => {
  const { driverBenefits } = mockData;

  const iconMap = {
    DollarSign,
    Eye,
    CreditCard,
    QrCode,
    Calculator,
    Zap
  };

  return (
    <section id="driver" className="py-20 bg-slate-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="bg-[#2D9CDB]/10 text-[#2D9CDB] border-[#2D9CDB]/20 mb-4">
            Untuk Driver
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            {driverBenefits.title}
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            {driverBenefits.subtitle}
          </p>
        </div>

        {/* Driver Image and Benefits */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          {/* Driver Image */}
          <div className="relative">
            <div className="relative overflow-hidden rounded-3xl shadow-2xl">
              <img 
                src={driverBenefits.driverImage}
                alt="Tourism Driver with Passengers"
                className="w-full h-[400px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4">
                  <h3 className="font-bold text-slate-900 mb-2">Driver Profesional</h3>
                  <p className="text-sm text-slate-600">Mengantar wisatawan ke merchant-merchant partner terpercaya</p>
                </div>
              </div>
            </div>
          </div>

          {/* Benefits Grid */}
          <div className="space-y-6">
            {driverBenefits.benefits.map((benefit, index) => {
              const IconComponent = iconMap[benefit.icon];
              return (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
                  <CardContent className="p-6">
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#2D9CDB] to-blue-100 rounded-full flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                        <IconComponent className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-slate-900 mb-2">{benefit.title}</h3>
                        <p className="text-slate-600">{benefit.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* How It Works Steps */}
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg mb-20">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-12">
            Cara Mendapatkan Komisi
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {driverBenefits.steps.map((step, index) => {
              const icons = [QrCode, Calculator, Zap];
              const IconComponent = icons[index];
              return (
                <div key={index} className="text-center group relative">
                  <div className="relative mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#2D9CDB] to-blue-100 rounded-full flex items-center justify-center mx-auto shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="h-10 w-10 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-[#2D9CDB] to-blue-200 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg">
                      {step.step}
                    </div>
                  </div>
                  <h4 className="text-lg font-bold text-slate-900 mb-3">{step.title}</h4>
                  <p className="text-slate-600">{step.description}</p>
                  {index < driverBenefits.steps.length - 1 && (
                    <div className="hidden md:block absolute top-10 left-1/2 transform translate-x-8">
                      <ArrowRight className="h-6 w-6 text-[#2D9CDB]" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Testimonials */}
        <div className="mb-12">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-12">
            Testimoni Driver
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {driverBenefits.testimonials.map((testimonial, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-6 text-lg italic">"{testimonial.content}"</p>
                  <div>
                    <p className="font-bold text-slate-900">{testimonial.name}</p>
                    <p className="text-slate-600">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            size="lg"
            onClick={() => window.open('https://mitra.salinguntung.com', '_blank')}
            className="bg-[#2D9CDB] hover:bg-[#2589C7] text-white px-8 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 group"
          >
            Bergabung Sebagai Driver
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default DriverSection;